import { type QueryClient, mutationOptions, queryOptions } from "@tanstack/react-query";
import { hc, InferRequestType, InferResponseType, parseResponse } from "hono/client";

import { amsterdamYear, freshForTheAmsterdamDay } from "~/lib/amsterdam-time";
import type { ClickAttribution } from "~/lib/attribution-cookie";
import { authClient } from "~/lib/auth";
import type { AppType } from "../../hono-api";

const GENERIC_API_ERROR = "Er ging iets mis. Probeer het opnieuw.";

const mapRawErrorToDutch = (rawMessage?: string) => {
  if (!rawMessage) {
    return GENERIC_API_ERROR;
  }

  const normalizedMessage = rawMessage.toLowerCase().trim();

  if (
    normalizedMessage.includes("failed to fetch") ||
    normalizedMessage.includes("networkerror")
  ) {
    return "We konden geen verbinding maken. Controleer je internetverbinding en probeer het opnieuw.";
  }

  if (normalizedMessage.includes("timeout")) {
    return "De verbinding duurde te lang. Probeer het opnieuw.";
  }

  if (
    normalizedMessage.includes("unauthorized") ||
    normalizedMessage.includes("forbidden") ||
    normalizedMessage.includes("not allowed")
  ) {
    return "Je hebt geen toegang tot deze actie.";
  }

  if (normalizedMessage.includes("not found")) {
    return "We konden de gevraagde gegevens niet vinden.";
  }

  if (
    normalizedMessage.includes("bad request") ||
    normalizedMessage.includes("internal server error") ||
    normalizedMessage.includes("invalid")
  ) {
    return GENERIC_API_ERROR;
  }

  return rawMessage;
};

const getErrorMessageFromPayload = (payload: unknown): string | undefined => {
  if (!payload || typeof payload !== "object") {
    return undefined;
  }

  if ("error" in payload && typeof payload.error === "string") {
    return payload.error;
  }

  if ("message" in payload && typeof payload.message === "string") {
    return payload.message;
  }

  return undefined;
};

/**
 * Error thrown for any non-OK API response. `message` is the Dutch, user-facing
 * string (backwards compatible with consumers that only read `error.message`),
 * while `status` and `payload` expose the raw HTTP status and parsed JSON body so
 * callers can branch on machine-readable fields like `code` / `remaining`.
 */
export class ApiError extends Error {
  status: number;
  payload: unknown;

  constructor(message: string, status: number, payload: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.payload = payload;
  }
}

const parseApiError = async (
  response: Response,
): Promise<{ message: string; payload: unknown }> => {
  const contentType = response.headers.get("content-type") ?? "";

  if (contentType.includes("application/json")) {
    const jsonBody = await response.json().catch(() => null);
    const mapped = mapRawErrorToDutch(getErrorMessageFromPayload(jsonBody) ?? undefined);
    return { message: mapped || GENERIC_API_ERROR, payload: jsonBody };
  }

  const errorText = await response.text().catch(() => "");
  const mappedMessage = mapRawErrorToDutch(errorText || response.statusText);
  return { message: mappedMessage || GENERIC_API_ERROR, payload: null };
};

export const client = hc<AppType>(import.meta.env.VITE_API_URL!, {
  fetch: async (input: RequestInfo | URL, init?: RequestInit) => {
    const session = await authClient.getSession();
    const authToken = session.data?.session.token;

    const response = await fetch(input, {
      ...init,
      headers: {
        ...(init?.body !== undefined &&
          !(init.body instanceof FormData) && {
            "Content-Type": "application/json",
          }),
        ...init?.headers,
        ...(authToken && { Authorization: `Bearer ${authToken}` }),
      },
      credentials: "include",
    });

    if (!response.ok) {
      const { message, payload } = await parseApiError(response);
      throw new ApiError(message, response.status, payload);
    }

    return response;
  },
});

export const providersListQueryOptions = () =>
  queryOptions({
    queryKey: ["providers", "list"],
    queryFn: () => {
      return parseResponse(client.api.chargers.providers.$get());
    },
    staleTime: 1000 * 60 * 10, // Cache data for 10 minutes
  });

export const chargersListQueryOptions = () =>
  queryOptions({
    queryKey: ["chargers", "list"],
    queryFn: () => {
      return parseResponse(client.api.chargers.$get());
    },
  });

export const ereRateQueryOptions = () =>
  queryOptions({
    queryKey: ["ere-rate"],
    queryFn: () => {
      return parseResponse(client.api["ere-rate"].$get());
    },
    staleTime: 1000 * 60 * 60, // Cache for 1 hour — rate updates once per day
  });

export const uploadStateQueryOptions = () =>
  queryOptions({
    queryKey: ["charger-documents", "upload-state"],
    queryFn: () => {
      return parseResponse(client.api["charger-documents"]["upload-state"].$get());
    },
    staleTime: 0,
    gcTime: 0,
  });

/**
 * Monthly session-document upload limit, resolved server-side. The backend is the
 * single source of truth for the cap, so the client no longer counts this month's
 * documents itself. `remaining` is how many uploads are still allowed this month.
 */
export const sessionUploadLimitQueryOptions = () =>
  queryOptions({
    queryKey: ["charger-documents", "session-upload-limit"],
    queryFn: () => {
      return parseResponse(
        client.api["charger-documents"]["session-upload-limit"].$get(),
      );
    },
  });

type ChargerDocumentsListEndpoint = (typeof client.api)["charger-documents"]["$post"];
type ChargerDocumentCategory =
  InferRequestType<ChargerDocumentsListEndpoint>["json"]["filter"]["category"];

export const chargerDocumentsListQueryOptions = (category: ChargerDocumentCategory) =>
  queryOptions({
    queryKey: ["charger-documents", "list", category],
    queryFn: () => {
      return parseResponse(
        client.api["charger-documents"].$post({ json: { filter: { category } } }),
      );
    },
  });

export const chargersModelsQueryOptions = (provider?: string) =>
  queryOptions({
    queryKey: ["chargers", "models", provider ?? "all"],
    queryFn: () => {
      return parseResponse(
        client.api.chargers.models.$get({ query: provider ? { provider } : {} }),
      );
    },
  });

export const chargersLinkMutationOptions = () =>
  mutationOptions({
    mutationFn: (data: { providerId: string; accountId?: string }) =>
      parseResponse(client.api.chargers.link.$post({ json: data })),
  });

type ChargersModelsEndpoint = (typeof client.api.chargers.models)["$get"];
/** A charger catalog model joined onto its brand (GET /api/chargers/models). */
export type RuntimeChargerModel = InferResponseType<
  ChargersModelsEndpoint,
  200
>["models"][number];

type ChargersDiscoverEndpoint = (typeof client.api.chargers.discover)["$post"];
/** A charger discovered on a provider account, before import (see ChargerSelector). */
export type DiscoveredCharger = InferResponseType<
  ChargersDiscoverEndpoint,
  200
>["chargers"][number];

/** Fetch (discover) chargers on a connected account without persisting anything. */
export const chargersDiscoverMutationOptions = () =>
  mutationOptions({
    mutationFn: (data: { providerId: string; accountId?: string }) =>
      parseResponse(client.api.chargers.discover.$post({ json: data })),
  });

type ChargersImportBody = InferRequestType<
  typeof client.api.chargers.import.$post
>["json"];

/** Import the selected chargers and trigger a historical backfill for the account. */
export const chargersImportMutationOptions = () =>
  mutationOptions({
    mutationFn: (data: ChargersImportBody) =>
      parseResponse(client.api.chargers.import.$post({ json: data })),
  });

/**
 * Inferred from the route rather than hand-written: the payload gained
 * `chargerModelId` and a duplicated literal type would have silently gone stale.
 */
type ChargerCreateBody = InferRequestType<typeof client.api.chargers.$post>["json"];

export const chargersCreateMutationOptions = () =>
  mutationOptions({
    mutationFn: (data: ChargerCreateBody) =>
      parseResponse(client.api.chargers.$post({ json: data })),
  });

export const chargersCreateOcppMutationOptions = () =>
  mutationOptions({
    mutationFn: (data: { brand: string; model: string; chargePointId: string }) =>
      parseResponse(client.api.chargers.ocpp.$post({ json: data })),
  });

/**
 * Issues a fresh OCPP connection token for a token-authenticated charger. The
 * token is returned once; the previous one stops connecting.
 */
export const chargersRotateOcppTokenMutationOptions = (
  accountId: string,
  chargerId: string,
) =>
  mutationOptions({
    mutationFn: () =>
      parseResponse(
        client.api.chargers[":accountId"][":chargerId"].ocpp.token.$post({
          param: { accountId, chargerId },
        }),
      ),
  });

/**
 * Downloads the Alfen .fwi root-certificate package. The endpoint needs the
 * bearer token, so a plain `<a href download>` can't reach it: fetch it through
 * the client and hand the browser a blob URL instead.
 */
export async function downloadAlfenCertificate(): Promise<void> {
  const response = await client.api.chargers.ocpp.alfen.certificate.$get();
  const fileName =
    /filename="([^"]+)"/.exec(response.headers.get("content-disposition") ?? "")?.[1] ??
    "lekkerladen-root-certificate.fwi";

  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

/** Upgrades an existing manual Peblar charger in place to the automatic (credential) flow. */
export const chargersUpgradePeblarMutationOptions = (
  accountId: string,
  chargerId: string,
) =>
  mutationOptions({
    mutationFn: (data: { chargerId: string; chargerPin: string }) =>
      parseResponse(
        client.api.chargers[":accountId"][":chargerId"].upgrade.peblar.$post({
          param: { accountId, chargerId },
          json: data,
        }),
      ),
  });

export const sessionsMonthlyListQueryOptions = () =>
  queryOptions({
    queryKey: ["sessions", "monthly"],
    queryFn: async () => {
      return parseResponse(client.api.sessions.monthly.$get());
    },
  });

type SessionsByYearEndpoint = (typeof client.api.sessions.years)[":year"]["$get"];
export type UserYearSessionRecord = InferResponseType<
  SessionsByYearEndpoint,
  200
>["data"][number];

/**
 * How long a session list stays in memory once nothing shows it: the rest of the day, so
 * going back to a screen reuses the year it already loaded instead of fetching it again.
 */
const SESSIONS_GC_TIME = 1000 * 60 * 60 * 24;

/**
 * Years (newest first) in which the user has sessions, by the year each started.
 *
 * Sessions change only when the nightly pipeline exports them, so these lists are fresh
 * for the whole day they were fetched on (`freshForTheAmsterdamDay`) and every screen
 * showing sessions shares them.
 */
export const sessionYearsQueryOptions = () =>
  queryOptions({
    queryKey: ["sessions", "years"],
    queryFn: () => parseResponse(client.api.sessions.years.$get()),
    staleTime: freshForTheAmsterdamDay,
    gcTime: SESSIONS_GC_TIME,
  });

/**
 * The year a session list opens on: the newest year the user has sessions in -- in early
 * January that is still last year -- or the current year when there are none yet.
 */
export const defaultSessionYear = (years: number[]): number =>
  years[0] ?? amsterdamYear(new Date());

/**
 * Loads the years and the year a session list opens on, for route loaders, so the screen
 * renders without waiting on two requests in a row.
 */
export const ensureDefaultSessionYear = async (queryClient: QueryClient) => {
  // `staleTime: "static"` for this call only: like `ensureQueryData`, use what is cached and
  // fetch only what is missing. The screens' own queries decide when it is stale.
  const years = await queryClient.query({
    ...sessionYearsQueryOptions(),
    staleTime: "static",
  });
  return queryClient.query({
    ...sessionsByYearQueryOptions(defaultSessionYear(years.data)),
    staleTime: "static",
  });
};

/**
 * Every session the user started in one year, newest first, in a single request: the
 * dashboard table and the month view on "Mijn laadpaal" both read from this, so moving
 * between them never fetches a year twice in a day.
 */
export const sessionsByYearQueryOptions = (year: number) =>
  queryOptions({
    queryKey: ["sessions", "years", year],
    queryFn: () =>
      parseResponse(
        client.api.sessions.years[":year"].$get({ param: { year: String(year) } }),
      ),
    staleTime: freshForTheAmsterdamDay,
    gcTime: SESSIONS_GC_TIME,
  });

type NotificationPostEndpoint =
  (typeof client.api.notifications)["keep-me-posted"]["$post"];

export const notificationCreateQueryOptions = (
  body: InferRequestType<NotificationPostEndpoint>["json"],
) =>
  queryOptions({
    queryKey: ["notifications", "create", body],
    queryFn: () =>
      parseResponse(client.api.notifications["keep-me-posted"].$post({ json: body })),
  });

export const currentContractQueryOptions = () =>
  queryOptions({
    queryKey: ["contracts", "current"],
    queryFn: () => parseResponse(client.api.contracts.current.$get()),
  });

export const contractTypesQueryOptions = () =>
  queryOptions({
    queryKey: ["contract-types"],
    queryFn: () => parseResponse(client.api["contract-types"].$get()),
    staleTime: 1000 * 60 * 60,
  });

type ContractTypesGetEndpoint = (typeof client.api)["contract-types"]["$get"];
export type ContractTypesResponse = InferResponseType<ContractTypesGetEndpoint, 200>;
export type AvailableContractType = ContractTypesResponse["contractTypes"][number];
export type AllowedTermOffer = ContractTypesResponse["terms"]["offers"][number];
export type VolumeTier = AvailableContractType["volumeTiers"][number];

type ContractsPostEndpoint = (typeof client.api.contracts)["$post"];
export type ContractSubmitBody = InferRequestType<ContractsPostEndpoint>["json"];
export type ContractDuration = ContractSubmitBody["duration"];
/** Body of a refused `POST /api/contracts`; `code` says why. */
export type ContractErrorBody = InferResponseType<ContractsPostEndpoint, 400 | 409>;

export const contractSubmit = async (
  body: ContractSubmitBody,
): Promise<{ ok: boolean; contractId: string; status: string }> => {
  return parseResponse(client.api.contracts.$post({ json: body }));
};

type ContractsGetEndpoint = (typeof client.api.contracts)[":contractId"]["$get"];
export type ContractDocument = InferResponseType<ContractsGetEndpoint>;

export const contractGetQuery = (contractId: string) =>
  queryOptions({
    queryKey: ["contracts", "get", contractId],
    queryFn: () =>
      parseResponse(client.api.contracts[":contractId"].$get({ param: { contractId } })),
  });

export const contractGetByTransactionQuery = (transactionId: string) =>
  queryOptions({
    queryKey: ["contracts", "get-by-transaction", transactionId],
    queryFn: () =>
      parseResponse(
        client.api.contracts["by-transaction"][":transactionId"].$get({
          param: { transactionId },
        }),
      ),
  });

export const partnerOnboardingMutationOptions = () =>
  mutationOptions({
    mutationFn: ({ referralId }: { referralId: string }) =>
      parseResponse(client.api.partners.onboarding.$post({ json: { referralId } })),
  });

export type PartnerCustomerEmailQuery = { referralId: string };

export const partnerCustomerEmailQueryOptions = (query: PartnerCustomerEmailQuery) =>
  queryOptions({
    queryKey: ["partners", "email", query],
    queryFn: () => parseResponse(client.api.partners.email.$get({ query })),
  });

export type PartnerCustomerOnboardingQuery = { referralId: string };

export const partnerCustomerOnboardingQueryOptions = (
  query: PartnerCustomerOnboardingQuery,
) =>
  queryOptions({
    queryKey: ["partners", "onboarding", query],
    queryFn: () => parseResponse(client.api.partners.onboarding.$get({ query })),
  });

export const currentPartnerReferralQueryOptions = () =>
  queryOptions({
    queryKey: ["partners", "referral", "current"],
    queryFn: () => parseResponse(client.api.partners.referral.current.$get()),
  });

// Profile API
export const profileQueryOptions = () =>
  queryOptions({
    queryKey: ["profile", "current"],
    queryFn: () => {
      return parseResponse(client.api.profiles.current.$get());
    },
  });

type ProfilePatchEndpoint = (typeof client.api.profiles.current)["$patch"];

export const profileUpdateMutationOptions = () =>
  mutationOptions({
    mutationFn: (data: InferRequestType<ProfilePatchEndpoint>["json"]) =>
      parseResponse(client.api.profiles.current.$patch({ json: data })),
  });

export const profileOnboardingFinishedMutationOptions = () =>
  mutationOptions({
    mutationFn: (data: ClickAttribution) =>
      parseResponse(
        client.api.profiles.current["onboarding-finished"].$post({ json: data }),
      ),
  });

/** Write-once overstapper preselection flag (#2124). */
export const claimOverstapperHook = async (
  source?: string,
): Promise<{
  ok: true;
  stored: boolean;
  alreadyStored: boolean;
}> =>
  parseResponse(
    client.api.profiles.current["overstapper-hook"].$post({
      json: { captured: true, source },
    }),
  );

export const addToAudienceMutationOptions = () =>
  mutationOptions({
    mutationFn: (data: { provider: string }) =>
      parseResponse(client.api.marketing["add-to-audience"].$post({ json: data })),
  });

// Referral API — member-to-member. Partner referrals live under /partners.
export const referralQueryOptions = () =>
  queryOptions({
    queryKey: ["referrals", "current"],
    queryFn: () => parseResponse(client.api.referrals.current.$get()),
  });

type ReferralClaimEndpoint = (typeof client.api.referrals.current)["$post"];
type ReferralClaimInput = InferRequestType<ReferralClaimEndpoint>["json"];

export const claimReferral = (data: ReferralClaimInput) =>
  parseResponse(client.api.referrals.current.$post({ json: data }));

export const referralClaimMutationOptions = () =>
  mutationOptions({ mutationFn: claimReferral });

type SaveReferralCodeEndpoint = (typeof client.api.referrals.current.code)["$put"];
type SaveReferralCodeInput = InferRequestType<SaveReferralCodeEndpoint>["json"];

/** Syncs the user's own Viral Loops code to their referral record. */
export const saveReferralCode = (data: SaveReferralCodeInput) =>
  parseResponse(client.api.referrals.current.code.$put({ json: data }));

// Child acquisition partner referral codes from the wervingsportaal. Distinct
// from Viral Loops friend codes above; the format tells them apart (see
// ~/lib/acquisition-partners). Dev only for now.
export const acquisitionReferralQueryOptions = () =>
  queryOptions({
    queryKey: ["acquisition-referrals", "current"],
    queryFn: () => parseResponse(client.api["acquisition-referrals"].current.$get()),
  });

type AcquisitionReferralClaimEndpoint =
  (typeof client.api)["acquisition-referrals"]["current"]["$post"];
type AcquisitionReferralClaimInput =
  InferRequestType<AcquisitionReferralClaimEndpoint>["json"];

export const claimAcquisitionReferral = (data: AcquisitionReferralClaimInput) =>
  parseResponse(client.api["acquisition-referrals"].current.$post({ json: data }));

export const acquisitionReferralClaimMutationOptions = () =>
  mutationOptions({ mutationFn: claimAcquisitionReferral });

// Promo code API — fixed, event-linked codes handed out offline (issue #1836).
// Distinct from both referral flows above: one code is shared by many customers.
// The redemption, if any, and whether a promo code may still be claimed: any
// contract closes every code (see CodeClaimBlock in the API).
export type PromoCodeStatus = InferResponseType<
  (typeof client.api)["promo-codes"]["current"]["$get"],
  200
>;
export type PromoCodeRedemption = NonNullable<PromoCodeStatus["redemption"]>;

export const promoCodeQueryOptions = () =>
  queryOptions({
    queryKey: ["promo-codes", "current"],
    queryFn: () => parseResponse(client.api["promo-codes"].current.$get()),
  });

type PromoCodeClaimEndpoint = (typeof client.api)["promo-codes"]["current"]["$post"];
type PromoCodeClaimInput = InferRequestType<PromoCodeClaimEndpoint>["json"];

export const claimPromoCode = (data: PromoCodeClaimInput) =>
  parseResponse(client.api["promo-codes"].current.$post({ json: data }));

export const promoCodeClaimMutationOptions = () =>
  mutationOptions({ mutationFn: claimPromoCode });

export type RewardProduct = InferResponseType<
  (typeof client.api.rewards.products)["$get"],
  200
>["products"][number];

export const rewardProductsQueryOptions = () =>
  queryOptions({
    queryKey: ["rewards", "products"],
    queryFn: () => parseResponse(client.api.rewards.products.$get()),
    staleTime: 1000 * 60 * 60,
  });

type ZipcodeGetEndpoint = (typeof client.api.zipcode)["$get"];
type ZipcodeLookupQuery = InferRequestType<ZipcodeGetEndpoint>["query"];

export const lookupZipcode = async (params: ZipcodeLookupQuery) => {
  return parseResponse(
    client.api.zipcode.$get({
      query: {
        zipcode: params.zipcode.trim(),
        houseNumber: params.houseNumber.trim(),
      },
    }),
  );
};

type EanGetEndpoint = (typeof client.api.ean)["$get"];
type EanLookupQuery = InferRequestType<EanGetEndpoint>["query"];

export const lookupEan = async (params: EanLookupQuery) => {
  return parseResponse(
    client.api.ean.$get({
      query: {
        zipcode: params.zipcode.trim(),
        houseNumber: params.houseNumber.trim(),
        ...(params.addition?.trim() && { addition: params.addition.trim() }),
      },
    }),
  );
};

export const accountsListQueryOptions = () =>
  queryOptions({
    queryKey: ["accounts", "list"],
    queryFn: () => {
      return parseResponse(client.api.accounts.$get());
    },
    staleTime: 1000 * 60 * 5,
  });

export const accountBackfillMutationOptions = () =>
  mutationOptions({
    mutationFn: (data: { providerId: string; accountId?: string }) =>
      parseResponse(client.api.accounts.backfill.$post({ json: data })),
  });

// Mirrors HardRejectionCode (api/src/services/charger-documents/file-validation.ts)
// — kept in sync manually since this endpoint bypasses the generated RPC client
// (see the docstring below) and can't infer it from `hono-api.d.ts` directly.
// A couple of members (`unknown_format`, `pdf_pending`) are not rejections
// but queued-for-review outcomes — see MANUAL_REVIEW_REASON_CODES.
export type HardRejectionCode =
  | "empty_file"
  | "no_header"
  | "no_date_column"
  | "no_volume_column"
  | "unreadable_binary"
  | "unknown_format"
  | "invalid_structure"
  | "pdf_pending"
  | "personal_vehicle_app_unsupported"
  | "daily_volume_unsupported"
  | "monthly_overview_unsupported"
  | "session_no_end_time_unsupported"
  | "session_date_no_year_unsupported"
  | "settlement_document_unsupported"
  | "energy_report_unsupported"
  | "charge_point_spec_unsupported"
  | "mini_vehicle_app_unsupported"
  | "bmw_vehicle_app_unsupported";

export interface SessionFileUploadResult {
  success: boolean;
  message: string;
  validated: boolean;
  results: {
    fileName: string;
    validated: boolean;
    reason?: string;
    reasonCode?: HardRejectionCode;
  }[];
}

/**
 * Uploads a single session-data file with upload-progress callbacks. Bypasses
 * the `hc<AppType>` RPC client (built on `fetch`) and issues the same request via
 * `XMLHttpRequest` instead, because `fetch` doesn't reliably expose upload
 * progress across browsers — `xhr.upload.onprogress` does.
 *
 * One file per call (not the whole batch) so each file in a multi-file upload
 * gets its own progress bar and resolves to accepted/rejected independently,
 * instead of the UI only finding out the outcome once every file in the batch
 * has been received and validated.
 */
export async function uploadSessionFileWithProgress(
  data: {
    file: File;
    accountId: string;
    providerId: string;
    chargerId: string;
    backoffice?: string;
  },
  onProgress: (percent: number) => void,
): Promise<SessionFileUploadResult> {
  const session = await authClient.getSession();
  const authToken = session.data?.session.token;

  const formData = new FormData();
  formData.append("files", data.file);
  formData.append("accountId", data.accountId);
  formData.append("providerId", data.providerId);
  formData.append("chargerId", data.chargerId);
  if (data.backoffice !== undefined) formData.append("backoffice", data.backoffice);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${import.meta.env.VITE_API_URL}/api/charger-documents/sessions`);
    xhr.withCredentials = true;
    if (authToken) xhr.setRequestHeader("Authorization", `Bearer ${authToken}`);

    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable) return;
      // Cap at 99% while bytes are still in flight — the request isn't actually
      // done until the server responds (validation + storage happen after the
      // last byte arrives), so the jump to 100% happens on completion below.
      onProgress(Math.min(99, Math.round((event.loaded / event.total) * 100)));
    };

    xhr.onload = () => {
      let payload: unknown = null;
      try {
        payload = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        payload = null;
      }

      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(100);
        resolve(payload as SessionFileUploadResult);
        return;
      }

      const message = mapRawErrorToDutch(getErrorMessageFromPayload(payload));
      reject(new ApiError(message, xhr.status, payload));
    };

    xhr.onerror = () => {
      reject(new ApiError(mapRawErrorToDutch("Failed to fetch"), 0, null));
    };

    xhr.send(formData);
  });
}

export const uploadChargerPhotosMutationOptions = () =>
  mutationOptions({
    mutationFn: (data: {
      files: File[];
      accountId: string;
      providerId: string;
      chargerId: string;
    }) =>
      parseResponse(
        client.api["charger-documents"].charger.$post({
          form: data,
        }),
      ),
  });

export const uploadEnergyInvoiceMutationOptions = () =>
  mutationOptions({
    mutationFn: (data: {
      file: File;
      accountId: string;
      providerId: string;
      chargerId: string;
    }) =>
      parseResponse(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (client.api["charger-documents"] as any)["energy-invoice"].$post({
          form: data,
        }),
      ),
  });

// Location proof (geolocation + live photo). Desktop create/poll need an authed
// session; the phone scan/submit are session-less (URL token = auth) — the client
// just sends no Authorization header when there is no session.

type LocationProofRequestsEndpoint =
  (typeof client.api)["charger-documents"]["location-proof"]["requests"]["$post"];

/** A created location-proof request: QR payload + charger identity. */
export type LocationProofRequest = InferResponseType<LocationProofRequestsEndpoint, 200>;

export const createLocationProofRequestMutationOptions = () =>
  mutationOptions({
    mutationFn: (data: { accountId: string; chargerId: string }) =>
      parseResponse(
        client.api["charger-documents"]["location-proof"].requests.$post({ json: data }),
      ),
  });

export const locationProofRequestStatusQueryOptions = (requestId: string) =>
  queryOptions({
    queryKey: ["location-proof", "request", requestId],
    queryFn: () =>
      parseResponse(
        client.api["charger-documents"]["location-proof"].requests[":requestId"].$get({
          param: { requestId },
        }),
      ),
  });

export const locationProofScanQueryOptions = (token: string) =>
  queryOptions({
    queryKey: ["location-proof", "scan", token],
    queryFn: () =>
      parseResponse(
        client.api["charger-documents"]["location-proof"].scan[":token"].$get({
          param: { token },
        }),
      ),
    retry: false,
  });

// Legal documents (terms & conditions, privacy policy, cookies, actievoorwaarden) —
// public, unauthenticated.
export type LegalDocumentType = "terms" | "privacy" | "cookies" | "actievoorwaarden";

export const activeLegalDocumentQueryOptions = (type: LegalDocumentType) =>
  queryOptions({
    queryKey: ["legal", type, "active"],
    queryFn: () => parseResponse(client.legal[type].active.$get()),
    retry: false,
  });

export const submitLocationProofMutationOptions = () =>
  mutationOptions({
    mutationFn: (data: {
      token: string;
      file: File;
      latitude: number;
      longitude: number;
      accuracy: number;
      capturedAt: string;
    }) =>
      parseResponse(
        client.api["charger-documents"]["location-proof"].scan[":token"].$post({
          param: { token: data.token },
          form: {
            file: data.file,
            latitude: data.latitude,
            longitude: data.longitude,
            accuracy: data.accuracy,
            capturedAt: data.capturedAt,
          },
        }),
      ),
  });
